import React, { useState, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSave, faDownload, faEdit, faSearch, faFilter, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import CommonButton from "../../elments/CommonButton";
const BulkPreview = ({ 
  data, 
  onBack, 
  onSaveAll, 
  onExportSPSS, 
  loading,
  initialQuery = '',
  initialShowMissing = false 
}) => {
  const [bulkQuery, setBulkQuery] = useState(initialQuery);
  const [showOnlyMissing, setShowOnlyMissing] = useState(initialShowMissing);
  const [bulkEditedData, setBulkEditedData] = useState(JSON.parse(JSON.stringify(data || {})));

  // Check if a question has missing logics
  const isQuestionMissing = (q) => {
    const hasQLogic = !!(q?.Logic && String(q.Logic).trim());
    const hasTerm = !!(q?.termLogic && String(q.termLogic).trim());
    const childMissing = hasMissingIn(q?.values) || 
                       hasMissingIn(q?.Rows) || 
                       hasMissingIn(q?.Cols) || 
                       hasMissingIn(q?.Statements);
    return !hasQLogic || !hasTerm || childMissing;
  };

  const hasMissingIn = (arr) => {
    if (!Array.isArray(arr) || arr.length === 0) return false;
    return arr.some((x) => !(x?.filterLogic && x.filterLogic.trim()));
  };

  // Get count of missing child logics
  const getMissingChildCount = (q) => {
    let count = 0;
    [q?.values, q?.Rows, q?.Cols, q?.Statements].forEach(arr => {
      if (Array.isArray(arr)) {
        count += arr.filter(x => !x?.filterLogic?.trim()).length;
      }
    });
    return count;
  };

  const bulkQuestionList = useMemo(() => {
    const entries = Object.entries(bulkEditedData || {});
    const filtered = entries.filter(([key, v]) => {
      const text = `${key} ${v?.Qtitle || ''}`.toLowerCase();
      const matchesQuery = text.includes(bulkQuery.toLowerCase());
      const missing = isQuestionMissing(v);
      return matchesQuery && (!showOnlyMissing || missing);
    });

    return filtered.sort((a, b) => a[0].localeCompare(b[0]));
  }, [bulkEditedData, bulkQuery, showOnlyMissing]);

  const handleBulkFieldChange = (qKey, field, value) => {
    setBulkEditedData(prev => ({
      ...prev,
      [qKey]: {
        ...prev[qKey],
        [field]: value
      }
    }));
  };

  const handleBulkChildLogicChange = (qKey, target, index, value) => {
    setBulkEditedData(prev => {
      const next = { ...prev };
      const q = next[qKey];
      if (!q || !Array.isArray(q[target])) return prev;
      
      const updatedArray = [...q[target]];
      if (updatedArray[index]) {
        updatedArray[index] = {
          ...updatedArray[index],
          filterLogic: value
        };
        next[qKey] = {
          ...q,
          [target]: updatedArray
        };
      }
      return next;
    });
  };

  const handleSaveAll = () => {
    onSaveAll(bulkEditedData);
  };

  const handleGoToQuestion = (qKey) => {
    const question = bulkEditedData[qKey];
    if (window.setSelectedTitle && window.setSelectedVariable && window.setShowBulkPreview) {
      window.setSelectedTitle(qKey);
      window.setSelectedVariable(question);
      window.setShowBulkPreview(false);
    }
  };

  return (
    <div className="simple-bulk-preview">
      <div className="simple-bulk-header">
        <div className="simple-bulk-title">
          <CommonButton
            title="Go back to main view"
            onClick={onBack}
            variant="primary"
            symbol={faArrowLeft}
            label="Back"
            className="back-button"
          />
          <h3>Bulk Logic Editor</h3>
        </div>
        
        <div className="simple-bulk-controls">
          <div className="simple-search">
            <FontAwesomeIcon icon={faSearch} />
            <input
              type="text"
              placeholder="Search variables..."
              value={bulkQuery}
              onChange={(e) => setBulkQuery(e.target.value)}
            />
          </div>
          
          <label className="simple-filter">
            <input
              type="checkbox"
              checked={showOnlyMissing}
              onChange={(e) => setShowOnlyMissing(e.target.checked)}
            />
            <FontAwesomeIcon icon={faFilter} />
            Show only missing
          </label>
          
          <div className="simple-actions">
            <CommonButton
              title="Save all changes"
              onClick={handleSaveAll}
              variant="success"
              symbol={faSave}
              label="Save All"
              className="save-all-btn"
            />
            <CommonButton
              title="Export SPSS syntax file"
              onClick={onExportSPSS}
              disabled={loading}
              variant="primary"
              symbol={faDownload}
              label="Export SPSS"
              className="export-btn"
            />
          </div>
        </div>
      </div>

      <div className="simple-bulk-table-container">
        <table className="simple-bulk-table">
          <thead>
            <tr>
              <th width="25%">Variable</th>
              <th width="30%">Logic</th>
              <th width="30%">Termination Logic</th>
              <th width="15%">Actions</th>
            </tr>
          </thead>
          <tbody>
            {bulkQuestionList.length === 0 ? (
              <tr>
                <td colSpan="4" className="no-results">
                  No variables found
                </td>
              </tr>
            ) : (
              bulkQuestionList.map(([qKey, q]) => {
                const isMissing = isQuestionMissing(q);
                const missingChildCount = getMissingChildCount(q);
                
                return (
                  <tr key={qKey} className={isMissing ? 'missing-row' : ''}>
                    <td>
                      <div className="variable-cell">
                        <div className="variable-name">
                          {qKey}
                        </div>
                        {q?.Qtitle && (
                          <div className="variable-title">
                            {q.Qtitle}
                          </div>
                        )}
                        {missingChildCount > 0 && (
                          <div className="missing-count">
                            Missing in {missingChildCount} items
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <input
                        type="text"
                        className="simple-logic-input"
                        placeholder="Filter logic..."
                        value={q?.Logic || ''}
                        onChange={(e) => handleBulkFieldChange(qKey, 'Logic', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className="simple-logic-input"
                        placeholder="Termination logic..."
                        value={q?.termLogic || ''}
                        onChange={(e) => handleBulkFieldChange(qKey, 'termLogic', e.target.value)}
                      />
                    </td>
                    <td>
                      <CommonButton
                        title="Edit question details"
                        onClick={() => handleGoToQuestion(qKey)}
                        variant="primary"
                        symbol={faEdit}
                        label="Edit Details"
                        className="edit-btn"
                      />
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default BulkPreview;